import React from "react";
import LegalPageLayout, { LegalList, LegalSection } from "./LegalPageLayout";
import { SUPPORT_EMAIL } from "../common/SiteFooter";

const Terms: React.FC = () => (
  <LegalPageLayout title="Terms & Conditions" lastUpdated="31 August 2026">
    <p>
      These Terms &amp; Conditions govern your access to and use of Astria (the
      &ldquo;Service&rdquo;). By creating an account, purchasing a subscription,
      or otherwise using the Service, you agree to be bound by these terms. If
      you do not agree, please do not use the Service.
    </p>

    <LegalSection heading="1. Eligibility">
      <p>
        You must be at least 18 years old, or the age of majority in your
        jurisdiction, to create an account or purchase a subscription. By using
        the Service you confirm that the information you provide is accurate and
        that you are legally able to enter into this agreement.
      </p>
    </LegalSection>

    <LegalSection heading="2. Your Account">
      <p>
        You are responsible for maintaining the confidentiality of your account
        credentials and for all activity that occurs under your account. Please
        notify us immediately at {SUPPORT_EMAIL} if you believe your account has
        been accessed without your permission.
      </p>
    </LegalSection>

    <LegalSection heading="3. Subscriptions and Billing">
      <LegalList
        items={[
          "Paid plans are billed in advance on a recurring basis according to the cadence shown on the plan you select at checkout.",
          "Payments are processed by Stripe. We do not store your full card details on our servers.",
          "Your subscription renews automatically at the end of each billing period unless you cancel before the renewal date.",
          "We may change plan pricing or features. Where a change affects an active subscription, we will give reasonable notice before it takes effect on your next renewal.",
        ]}
      />
    </LegalSection>

    <LegalSection heading="4. Cancellation and Refunds">
      <p>
        You may cancel your subscription at any time. Cancellation and refund
        eligibility are described in our Refund &amp; Cancellation Policy, which
        forms part of these terms.
      </p>
    </LegalSection>

    <LegalSection heading="5. Acceptable Use">
      <p>You agree not to:</p>
      <LegalList
        items={[
          "Use the Service for any unlawful purpose or in breach of any applicable regulation.",
          "Attempt to gain unauthorised access to the Service, other accounts, or our systems.",
          "Resell, redistribute, or commercially exploit content generated through the Service without our written permission.",
          "Interfere with or disrupt the integrity or performance of the Service.",
        ]}
      />
    </LegalSection>

    <LegalSection heading="6. Nature of the Content">
      <p>
        Astria provides guidance, reflections, and insights for personal
        interest and self-reflection. The Service does not provide medical,
        psychological, legal, or financial advice, and its content should not be
        relied upon as a substitute for professional advice. Decisions you make
        based on the Service are your own responsibility.
      </p>
    </LegalSection>

    <LegalSection heading="7. Intellectual Property">
      <p>
        The Service, including its software, design, text, and branding, remains
        our property or that of our licensors. You are granted a limited,
        non-exclusive, non-transferable right to use the Service for your own
        personal, non-commercial purposes for the duration of your subscription.
      </p>
    </LegalSection>

    <LegalSection heading="8. Suspension and Termination">
      <p>
        We may suspend or terminate your access if you breach these terms or if
        we are required to do so by law. You may stop using the Service and
        close your account at any time.
      </p>
    </LegalSection>

    <LegalSection heading="9. Limitation of Liability">
      <p>
        To the fullest extent permitted by law, the Service is provided on an
        &ldquo;as is&rdquo; basis without warranties of any kind. We are not
        liable for indirect, incidental, or consequential losses. Nothing in
        these terms excludes liability that cannot be excluded by law.
      </p>
    </LegalSection>

    <LegalSection heading="10. Changes to These Terms">
      <p>
        We may update these terms from time to time. The &ldquo;last
        updated&rdquo; date above reflects the current version, and continued
        use of the Service after an update constitutes acceptance of the revised
        terms.
      </p>
    </LegalSection>

    <LegalSection heading="11. Contact">
      <p>
        Questions about these terms can be sent to{" "}
        <a
          href={`mailto:${SUPPORT_EMAIL}`}
          className="font-medium text-[#1B1511] underline underline-offset-4"
        >
          {SUPPORT_EMAIL}
        </a>
        .
      </p>
    </LegalSection>
  </LegalPageLayout>
);

export default Terms;
